import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { useKioskTheme } from './hooks/useKioskTheme';
import styles from './kiosk.module.css';

export interface KioskTabBarProps {
  readonly tabs: string[];
  readonly activeTab: string | null;
  readonly ticketLabels: Record<string, string>;
  readonly onActivate: (encId: string) => void;
  readonly onClose: (encId: string) => void;
  readonly onNewTicket: () => void;
  readonly onOpenExisting: () => void;
}

export function KioskTabBar({
  tabs,
  activeTab,
  ticketLabels,
  onActivate,
  onClose,
  onNewTicket,
  onOpenExisting,
}: KioskTabBarProps) {
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const [pickerRect, setPickerRect] = useState<DOMRect | null>(null);
  const { theme } = useKioskTheme();

  useEffect(() => {
    if (!showPicker) return undefined;
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      const inPanel = pickerRef.current && pickerRef.current.contains(target);
      const onBtn = addBtnRef.current && addBtnRef.current.contains(target);
      if (!inPanel && !onBtn) {
        setShowPicker(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showPicker]);

  const handleAddClick = (): void => {
    if (addBtnRef.current) {
      setPickerRect(addBtnRef.current.getBoundingClientRect());
    }
    setShowPicker((v) => !v);
  };

  return (
    <div className={styles.tabBar}>
      <div className={styles.tabList}>
        {tabs.map((encId) => (
          <div
            key={encId}
            className={`${styles.tab} ${encId === activeTab ? styles.tabActive : ''}`}
          >
            <button
              type="button"
              className={styles.tabLabel}
              onClick={() => onActivate(encId)}
              aria-pressed={encId === activeTab}
            >
              {ticketLabels[encId] ?? 'Draft'}
            </button>
            <button
              type="button"
              className={styles.tabClose}
              onClick={() => onClose(encId)}
              aria-label={`Close tab ${ticketLabels[encId] ?? 'Draft'}`}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div style={{ position: 'relative', flex: '0 0 auto' }}>
        <button
          ref={addBtnRef}
          type="button"
          className={styles.tabNew}
          onClick={handleAddClick}
          aria-label="Add ticket"
          title="Add ticket"
        >
          +
        </button>
        {showPicker && pickerRect && createPortal(
          <div
            ref={pickerRef}
            data-kiosk-theme={theme}
            className={styles.tabAddPicker}
            style={{
              position: 'fixed',
              top: pickerRect.bottom + 4,
              right: window.innerWidth - pickerRect.right,
              zIndex: 1000,
            }}
          >
            <button
              type="button"
              className={styles.tabAddPickerOption}
              onClick={() => { setShowPicker(false); onNewTicket(); }}
            >
              New ticket
            </button>
            <button
              type="button"
              className={styles.tabAddPickerOption}
              onClick={() => { setShowPicker(false); onOpenExisting(); }}
            >
              Open existing
            </button>
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
}
